import {apiClient} from '@/shared/services/apiClient';
import type {ProviderOrderSort} from '@mld/shared/constants/providerOrders';
import type {ProviderOrder, Test} from '@/shared/types/api';

export interface ProviderOrdersResponse {
    orders: ProviderOrder[];
    total: number;
    page: number;
    pageSize: number;
}

export interface ProviderPatientDataResponse {
    orders: Test[];
    categoryScores: Record<string, number>;
    subscription: boolean;
    patient?: {
        id: number;
        firstName?: string | null;
        lastName?: string | null;
        gender?: string | null;
    } | null;
}

export interface ProviderOrderSearchRequest {
    oid?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    limit?: number;
    page?: number;
    sort?: ProviderOrderSort;
}

export interface ProviderSalesPoint {
    date: string;
    label: string;
    salesAmount: number;
    ordersCount: number;
}

export interface ProviderSalesReportResponse {
    range: 'last7' | 'month' | 'year' | 'custom';
    startDate: string;
    endDate: string;
    salesThisPeriod: number;
    avgDailySales: number;
    ordersPlaced: number;
    conversionRate: number | null;
    series: ProviderSalesPoint[];
}

export interface ProviderConsoleConfigResponse {
    modules: Array<{module_id: number; enabled: boolean}>;
    permissions: string[];
}

interface ProviderOrderParams {
    orgId?: number;
    limit?: number;
    page?: number;
    sort?: ProviderOrderSort;
}

interface ProviderSalesParams {
    orgId?: number;
    range?: ProviderSalesReportResponse['range'];
    from?: string;
    to?: string;
}

interface PatientLaunchTokenResponse {
    token: string;
}

interface PatientLaunchExchangeResponse {
    token: string;
    userInfo: unknown;
    username?: string;
}

export const ProviderConsoleService = {
    async getPatientOrders(params: ProviderOrderParams = {}): Promise<ProviderOrdersResponse> {
        return apiClient.get<ProviderOrdersResponse>('/api/provider/orders', {
            params,
        });
    },

    async searchPatientOrders(body: ProviderOrderSearchRequest): Promise<ProviderOrdersResponse> {
        return apiClient.post<ProviderOrdersResponse>('/api/provider/orders/search', body);
    },

    async createPatientLaunchToken(userId: number): Promise<PatientLaunchTokenResponse> {
        return apiClient.post<PatientLaunchTokenResponse>('/api/provider/patient-launch', {userId});
    },

    async exchangePatientLaunchToken(token: string): Promise<PatientLaunchExchangeResponse> {
        return apiClient.post<PatientLaunchExchangeResponse>('/api/provider/patient-launch/exchange', {token});
    },

    async getSalesReport(params: ProviderSalesParams = {}): Promise<ProviderSalesReportResponse> {
        return apiClient.get<ProviderSalesReportResponse>('/api/provider/sales', {
            params,
        });
    },

    async getConsoleConfig(): Promise<ProviderConsoleConfigResponse> {
        return apiClient.get<ProviderConsoleConfigResponse>('/api/provider/console-config');
    },

    async getPatientData(userId: number): Promise<ProviderPatientDataResponse> {
        return apiClient.get<ProviderPatientDataResponse>(`/api/provider/patient/${userId}/data`);
    },
};
